import apiClient from './client';

export interface MBCBReport {
  id: number;
  planterId: number;
  reportMonth: number;
  reportYear: number;
  status: 'draft' | 'submitted' | 'under_review' | 'approved' | 'needs_revision' | 'rejected';
  sundayAttendance?: number;
  smallGroupAttendance?: number;
  newMembers?: number;
  baptisms?: number;
  salvations?: number;
  monthlyIncome?: number;
  monthlyExpenses?: number;
  buildingFund?: number;
  missionsGiving?: number;
  ministryHighlights?: string;
  communityOutreach?: string;
  discipleshipActivities?: string;
  leadershipDevelopment?: string;
  currentChallenges?: string;
  prayerRequests?: string;
  supportNeeded?: string;
  shortTermGoals?: string;
  longTermVision?: string;
  nextMonthFocus?: string;
  volunteerHours?: number;
  communityPartnerships?: number;
  specialEvents?: string;
  additionalNotes?: string;
  attendanceNotes?: string;
  givingNotes?: string;
  weeklyMetrics?: Record<string, Record<string, number>>;
  givingBreakdown?: {
    income?: Record<string, number | null>;
    missionGiving?: Record<string, number | null>;
  };
  source?: string;
  externalId?: string | null;
  importBatchId?: string | null;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: number;
  reviewNotes?: string;
  planter?: {
    id: number;
    name: string;
    spouseName?: string;
    churchName?: string;
  };
  submitter?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  reviewer?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ReportFilters {
  page?: number;
  limit?: number;
  year?: number;
  month?: number;
  status?: string;
  planterId?: number;
  search?: string;
}

/**
 * Aggregate totals for approved reports in the selected period.
 * Postgres SUM/AVG/COUNT return strings over the wire, so values may be string | null.
 */
export interface ReportStats {
  reportCount?: number | string | null;
  totalAttendance?: number | string | null;
  avgAttendance?: number | string | null;
  totalBaptisms?: number | string | null;
  totalSalvations?: number | string | null;
  totalNewMembers?: number | string | null;
  totalIncome?: number | string | null;
  totalExpenses?: number | string | null;
  totalMissionsGiving?: number | string | null;
}

export interface ReportRequestPayload {
  reportMonth: number;
  reportYear: number;
  planterId?: number;
  plantingStepScope?: 'funding' | 'funding_or_post_funding';
}

export interface ReportRequestResult {
  createdCount: number;
  skippedCount: number;
  period: {
    month: number;
    year: number;
  };
  mode: 'bulk' | 'single';
  createdReportIds?: number[];
}

class MBCBService {
  async getAllReports(filters: ReportFilters = {}) {
    const response = await apiClient.get('/mbcb/reports', { params: filters });
    return response.data;
  }

  async getReport(id: number) {
    const response = await apiClient.get(`/mbcb/reports/${id}`);
    return response.data;
  }

  async getPendingReports() {
    const response = await apiClient.get('/mbcb/reports/pending');
    return response.data;
  }

  async getReportStats(year: number, month?: number) {
    const response = await apiClient.get('/mbcb/reports/stats', {
      params: { year, month },
    });
    return response.data;
  }

  async approveReport(id: number, notes?: string) {
    const response = await apiClient.post(`/mbcb/reports/${id}/approve`, { notes });
    return response.data;
  }

  async requestRevision(id: number, notes: string) {
    const response = await apiClient.post(`/mbcb/reports/${id}/request-revision`, { notes });
    return response.data;
  }

  async deleteReport(id: number) {
    const response = await apiClient.delete(`/mbcb/reports/${id}`);
    return response.data;
  }

  async bulkDeleteReports(ids: number[]) {
    const response = await apiClient.post('/mbcb/reports/bulk-delete', { ids });
    return response.data as {
      success: boolean;
      message?: string;
      data: {
        deletedIds: number[];
        notDeleted: { id: number; reason: string }[];
      };
    };
  }

  async remindUnsubmittedReports() {
    const response = await apiClient.post('/mbcb/reports/remind-unsubmitted');
    return response.data as {
      success: boolean;
      message?: string;
      data: {
        consideredCount: number;
        sentCount: number;
        skippedCount: number;
        failedCount: number;
        sentReportIds: number[];
        skipped: { planterId: number; reportId: number; reason?: string }[];
      };
    };
  }

  async requestReports(payload: ReportRequestPayload) {
    const response = await apiClient.post('/mbcb/reports/request', payload);
    return response.data as {
      success: boolean;
      message?: string;
      data: ReportRequestResult;
    };
  }

  async importReports(payload: {
    csvText: string;
    dryRun?: boolean;
    onDuplicate?: 'skip' | 'update';
    defaultStatus?: string;
  }) {
    const response = await apiClient.post('/mbcb/reports/import', payload);
    return response.data;
  }

  async exportReport(id: number): Promise<Blob> {
    const response = await apiClient.get(`/mbcb/reports/${id}/export`, {
      responseType: 'blob',
    });
    return response.data;
  }
}

export const mbcbService = new MBCBService();

